import Cropper from 'react-easy-crop';
import { useState, useCallback } from 'react';
import { RotateLeft } from '@mui/icons-material';
import { type Area, type Point } from 'react-easy-crop';
import {
  Box,
  Stack,
  Paper,
  Button,
  useTheme,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { UploadFile } from 'types';
import { DropZone } from 'components/drop-zone';
import { DropFile } from 'components/drop-file';

import getCroppedImg from './cropImage';
import { FileDropboxProps } from './types';

const FileDropbox = ({
  onClose,
  setFile,
  withoutCrop,
  helperContent,
  allowedFileSize = 10,
  accept = {
    'image/png': [],
    'image/jpg': [],
    'image/jpeg': [],
    'image/svg+xml': [],
  },
}: FileDropboxProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [fileToUpload, setFileToUpload] = useState<UploadFile | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  const isImageFile = fileToUpload?.file?.type.startsWith('image/') ?? false;
  const shouldCrop = isImageFile && !withoutCrop;

  const loadCroppedImage = useCallback(async () => {
    try {
      if (!fileToUpload) throw new Error();

      if (!shouldCrop) {
        setFile(fileToUpload);
        onClose();
        return;
      }

      const croppedImage = await getCroppedImg(
        fileToUpload.src,
        croppedAreaPixels,
        rotation
      );

      if (typeof croppedImage?.src === 'string') {
        const file = new File([croppedImage.blob], fileToUpload.file.name, {
          type: croppedImage.blob.type,
        });

        setFile({
          file,
          src: croppedImage.src,
        });
        onClose();
      }
    } catch {
      setIsError(true);
      setRotation(0);
      setFile(null);
      setFileToUpload(null);
    }
  }, [croppedAreaPixels, rotation, fileToUpload, setFile, onClose, shouldCrop]);

  const handleDeleteFile = () => {
    setFileToUpload(null);
    setZoom(1);
    setRotation(0);
  };

  const handleRotate = () => {
    setRotation((prev) => prev + 90);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        width: '100%',
        maxWidth: 532,
        minWidth: 300,
        display: 'flex',
        borderRadius: 2,
        position: 'relative',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        '& .crop-container': {
          borderRadius: 1,
        },
        '& .crop-area': {
          border: 'none',
          color: 'primary.main',
        },
      }}
    >
      <Typography
        variant="h6"
        component="h3"
        sx={{
          mb: 3,
          fontWeight: 600,
        }}
      >
        Add file
      </Typography>
      <Box
        sx={{
          mt: 3,
          height: 290,
          width: '100%',
          display: 'flex',
          position: 'relative',
          mb: fileToUpload && shouldCrop ? 0 : 3,
        }}
      >
        {fileToUpload ? (
          <>
            {shouldCrop && (
              <Cropper
                aspect={1}
                crop={crop}
                zoom={zoom}
                showGrid={false}
                cropShape="round"
                rotation={rotation}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                image={fileToUpload.src}
                objectFit="vertical-cover"
                onCropComplete={(_, croppedPixels: Area) =>
                  setCroppedAreaPixels(croppedPixels)
                }
                classes={{
                  cropAreaClassName: 'crop-area',
                  containerClassName: 'crop-container',
                }}
              />
            )}
            {!shouldCrop && isImageFile && (
              <img
                src={fileToUpload.src}
                className="w-height w-full object-contain"
              />
            )}
            {!shouldCrop && !isImageFile && (
              <Box
                sx={{
                  p: 2,
                  border: 1,
                  width: '100%',
                  borderRadius: 1,
                  display: 'flex',
                  textAlign: 'center',
                  alignItems: 'center',
                  borderStyle: 'dashed',
                  borderColor: 'grey.400',
                  justifyContent: 'center',
                  bgcolor: 'background.default',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Selected file: {fileToUpload.file.name}
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <DropZone
            errorMessage
            accept={accept}
            isError={isError}
            isLoading={isLoading}
            children={<DropFile />}
            setIsError={setIsError}
            setIsLoading={setIsLoading}
            allowedFileSize={allowedFileSize}
            setValue={(value) => setFileToUpload(value as UploadFile)}
          />
        )}
      </Box>
      {helperContent && <Box sx={{ mb: 3 }}>{helperContent}</Box>}
      {fileToUpload && shouldCrop && (
        <Box
          sx={{
            mb: 2,
            mt: 0.5,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            variant="text"
            onClick={handleRotate}
            startIcon={<RotateLeft />}
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              },
            }}
          >
            Rotate
          </Button>
        </Box>
      )}
      <Stack
        spacing={3}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: fileToUpload ? 'space-between' : 'flex-end',
        }}
      >
        {fileToUpload && (
          <Button
            variant="contained"
            onClick={handleDeleteFile}
            sx={{
              fontWeight: 600,
              bgcolor: 'error.main',
              textTransform: 'none',
              color: 'error.contrastText',
              width: isMobile ? '100%' : 112,
              '&:hover': {
                bgcolor: 'error.dark',
              },
            }}
          >
            Delete
          </Button>
        )}

        <Stack
          spacing={3}
          direction={isMobile ? 'column' : 'row'}
          sx={{
            width: isMobile ? '100%' : 'auto',
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              borderColor: 'grey.400',
              color: 'text.secondary',
              width: isMobile ? '100%' : 112,
              '&:hover': {
                bgcolor: 'grey.50',
                borderColor: 'grey.600',
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={!fileToUpload}
            onClick={loadCroppedImage}
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              width: isMobile ? '100%' : 112,
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '&:disabled': {
                color: 'action.disabled',
                bgcolor: 'action.disabledBackground',
              },
            }}
          >
            Save
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export { FileDropbox };
